/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProcessGuide } from '@/types/process-guides.types';

interface ProcessFlowchartProps {
  guide: ProcessGuide;
  activeStepId?: string | null;
  onStepClick?: (stepId: string) => void;
}

interface NodePosition {
  id: string;
  x: number;
  y: number;
}

const NODE_W = 120;
const NODE_H = 40;
const GAP_X = 60;
const BRANCH_OFFSET_Y = 60;
const PADDING = 20;

function layoutNodes(guide: ProcessGuide): { nodes: NodePosition[]; width: number; height: number } {
  const positions = new Map<string, NodePosition>();
  const visited = new Set<string>();
  const stepMap = new Map(guide.steps.map((s) => [s.id, s]));

  // BFS to assign columns (x) and handle branching (y offsets)
  const queue: { id: string; col: number; row: number }[] = [];
  const firstStep = guide.steps[0];
  if (!firstStep) return { nodes: [], width: PADDING * 2, height: PADDING * 2 + NODE_H };

  queue.push({ id: firstStep.id, col: 0, row: 0 });
  visited.add(firstStep.id);

  let maxCol = 0;
  let maxRow = 0;

  while (queue.length > 0) {
    const item = queue.shift()!;
    const { id, col, row } = item;

    positions.set(id, {
      id,
      x: PADDING + col * (NODE_W + GAP_X),
      y: PADDING + row * BRANCH_OFFSET_Y,
    });

    if (col > maxCol) maxCol = col;
    if (row > maxRow) maxRow = row;

    const step = stepMap.get(id);
    if (step?.nextSteps) {
      step.nextSteps.forEach((nextId, i) => {
        if (!visited.has(nextId)) {
          visited.add(nextId);
          queue.push({ id: nextId, col: col + 1, row: row + i });
        }
      });
    }
  }

  // Add any steps not reached by BFS (disconnected)
  guide.steps.forEach((s) => {
    if (!positions.has(s.id)) {
      maxCol++;
      positions.set(s.id, {
        id: s.id,
        x: PADDING + maxCol * (NODE_W + GAP_X),
        y: PADDING,
      });
    }
  });

  const nodes = guide.steps.map((s) => positions.get(s.id)!).filter(Boolean);
  const width = PADDING * 2 + (maxCol + 1) * NODE_W + maxCol * GAP_X;
  const height = PADDING * 2 + (maxRow + 1) * NODE_H + maxRow * BRANCH_OFFSET_Y;

  return { nodes, width: Math.max(width, 200), height: Math.max(height, NODE_H + PADDING * 2) };
}

const ProcessFlowchart: React.FC<ProcessFlowchartProps> = ({ guide, activeStepId, onStepClick }) => {
  const { t } = useTranslation('platform');

  const { nodes, width, height } = useMemo(() => layoutNodes(guide), [guide]);

  const nodeMap = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const stepMap = useMemo(() => new Map(guide.steps.map((s) => [s.id, s])), [guide.steps]);

  // Build edges
  const edges = useMemo(() => {
    const result: { from: NodePosition; to: NodePosition }[] = [];
    guide.steps.forEach((step) => {
      const fromNode = nodeMap.get(step.id);
      if (!fromNode || !step.nextSteps) return;
      step.nextSteps.forEach((nextId) => {
        const toNode = nodeMap.get(nextId);
        if (toNode) result.push({ from: fromNode, to: toNode });
      });
    });
    return result;
  }, [guide.steps, nodeMap]);

  return (
    <div className="guide-flowchart-wrapper">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="guide-flowchart-svg"
      >
        <defs>
          <marker
            id={`arrow-${guide.code}`}
            markerWidth="8"
            markerHeight="6"
            refX="8"
            refY="3"
            orient="auto"
            markerUnits="userSpaceOnUse"
          >
            <path d="M0,0 L8,3 L0,6 Z" className="guide-flowchart-arrowhead" />
          </marker>
        </defs>

        {/* Edges */}
        {edges.map((edge, i) => {
          const x1 = edge.from.x + NODE_W;
          const y1 = edge.from.y + NODE_H / 2;
          const x2 = edge.to.x;
          const y2 = edge.to.y + NODE_H / 2;
          const cx1 = x1 + (x2 - x1) * 0.4;
          const cx2 = x1 + (x2 - x1) * 0.6;
          return (
            <path
              key={i}
              d={`M${x1},${y1} C${cx1},${y1} ${cx2},${y2} ${x2},${y2}`}
              className="guide-flowchart-edge"
              markerEnd={`url(#arrow-${guide.code})`}
            />
          );
        })}

        {/* Nodes */}
        {nodes.map((node) => {
          const isActive = activeStepId === node.id;
          const step = stepMap.get(node.id);
          const label = step
            ? t(`guides.${guide.code}.steps.${node.id}.title`, node.id)
            : node.id;

          return (
            <g
              key={node.id}
              className={`guide-flowchart-node${isActive ? ' guide-flowchart-node--active' : ''}`}
              onClick={() => onStepClick?.(node.id)}
              style={{ cursor: onStepClick ? 'pointer' : 'default' }}
            >
              <rect
                x={node.x}
                y={node.y}
                width={NODE_W}
                height={NODE_H}
                rx={8}
                ry={8}
                className={`guide-flowchart-rect${isActive ? ' guide-flowchart-rect--active' : ''}`}
              />
              <foreignObject x={node.x} y={node.y} width={NODE_W} height={NODE_H}>
                <div className="guide-flowchart-label">{label}</div>
              </foreignObject>
            </g>
          );
        })}
      </svg>

      <style>{`
        .guide-flowchart-wrapper {
          overflow-x: auto;
          padding: 0.5rem 0;
        }
        .guide-flowchart-svg {
          display: block;
          min-width: 100%;
        }
        .guide-flowchart-edge {
          fill: none;
          stroke: var(--color-border-default);
          stroke-width: 1.5;
        }
        .guide-flowchart-arrowhead {
          fill: var(--color-border-default);
        }
        .guide-flowchart-rect {
          fill: var(--color-bg-secondary);
          stroke: var(--color-border-default);
          stroke-width: 1.5;
        }
        .guide-flowchart-rect--active {
          stroke: var(--color-brand-primary);
          stroke-width: 2;
          filter: drop-shadow(0 0 4px var(--color-brand-primary));
        }
        .guide-flowchart-label {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          font-size: 11px;
          font-weight: 500;
          color: var(--color-text-primary);
          text-align: center;
          padding: 2px 6px;
          box-sizing: border-box;
          line-height: 1.2;
          overflow: hidden;
        }
        .guide-flowchart-node {
          transition: opacity 0.15s ease;
        }
        .guide-flowchart-node:hover .guide-flowchart-rect {
          stroke: var(--color-brand-primary);
        }
      `}</style>
    </div>
  );
};

export default ProcessFlowchart;
